// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {object} MediaGalleryRefs
 * @property {HTMLElement} viewerViewport
 * @property {HTMLElement} viewerContainer
 * @property {HTMLElement[]} [viewerSlides]
 * @property {HTMLButtonElement | undefined} prevButton
 * @property {HTMLButtonElement | undefined} nextButton
 * @property {HTMLElement | undefined} thumbnailViewport
 * @property {HTMLElement | undefined} thumbnailContainer
 * @property {HTMLButtonElement[]} [thumbnailButtons]
 * @property {HTMLElement | undefined} liveRegion
 */

/**
 * Media gallery with synced thumbnails powered by Embla Carousel.
 *
 * @extends Component<MediaGalleryRefs>
 */
class MediaGalleryComponent extends Component {
  /** @type {any} */
  #emblaMain;
  /** @type {any} */
  #emblaThumbs;
  /** @type {AbortController | undefined} */
  #variantAbortController;

  connectedCallback() {
    super.connectedCallback();
    this.#initEmbla();
    this.#registerVariantEvents();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#destroyEmbla();
    this.#initEmbla();
    this.#registerVariantEvents();
  }

  disconnectedCallback() {
    this.#destroyEmbla();
    this.#variantAbortController?.abort();
    super.disconnectedCallback();
  }

  scrollPrev() {
    this.#scrollBy(-1);
  }

  scrollNext() {
    this.#scrollBy(1);
  }

  /**
   * Handles thumbnail click events.
   * @param {{ index?: number }} data
   */
  handleThumbnailClick(data) {
    const index = Number(data?.index);
    if (!Number.isNaN(index)) {
      this.#scrollToIndex(index);
    }
  }

  /**
   * Sets the active media by DOM id.
   * @param {string} mediaDomId
   * @param {{ animate?: boolean }} [options]
   */
  setActiveMedia(mediaDomId, options = {}) {
    const slides = this.refs.viewerSlides ?? [];
    const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaDomId);
    if (index < 0) return;

    if (this.#emblaMain) {
      const jump = options.animate === false;
      this.#emblaMain.scrollTo(index, jump);
    } else {
      this.#setActiveClasses(index);
      this.#announceSlide(index);
    }
  }

  /**
   * Registers listeners for variant updates so the gallery syncs to the featured media.
   */
  #registerVariantEvents() {
    this.#variantAbortController?.abort();
    this.#variantAbortController = new AbortController();
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#variantAbortController.signal,
    });
  }

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (!event.detail?.resource?.featured_media?.id) return;
    const mediaDomId = `${this.dataset.sectionId || this.dataset.section}-${event.detail.resource.featured_media.id}`;
    this.setActiveMedia(mediaDomId);
  };

  #initEmbla() {
    const viewport = this.refs.viewerViewport;
    if (!viewport) return;

    const EmblaCarousel = /** @type {any} */ (window).EmblaCarousel;
    if (typeof EmblaCarousel !== 'function') {
      requestIdleCallback(() => this.#initEmbla());
      return;
    }

    this.#emblaMain = EmblaCarousel(viewport, {
      align: 'start',
      loop: this.dataset.loop === 'true',
      containScroll: 'trimSnaps',
    });
    this.#emblaMain.on('select', this.#handleMainSelect);
    this.#emblaMain.on('reInit', this.#handleMainSelect);
    this.#emblaMain.on('reInit', this.#updateNavButtons);
    this.#emblaMain.on('init', this.#updateNavButtons);
    this.#updateNavButtons();

    if (this.refs.thumbnailViewport) {
      this.#emblaThumbs = EmblaCarousel(this.refs.thumbnailViewport, {
        containScroll: 'keepSnaps',
        dragFree: true,
        align: 'start',
      });
    }

    const initialMediaId = this.dataset.initialMediaId;
    if (initialMediaId) {
      this.setActiveMedia(initialMediaId, { animate: false });
    } else {
      this.#handleMainSelect();
    }
  }

  #destroyEmbla() {
    this.#emblaMain?.destroy();
    this.#emblaThumbs?.destroy();
    this.#emblaMain = undefined;
    this.#emblaThumbs = undefined;
  }

  #handleMainSelect = () => {
    const selectedIndex = this.#emblaMain?.selectedScrollSnap?.() ?? 0;
    this.#setActiveClasses(selectedIndex);
    this.#announceSlide(selectedIndex);
    if (this.#emblaThumbs) {
      this.#emblaThumbs.scrollTo(selectedIndex);
    }
    this.#updateNavButtons();
  };

  #updateNavButtons = () => {
    if (!this.#emblaMain) return;
    const canScrollPrev = this.#emblaMain.canScrollPrev();
    const canScrollNext = this.#emblaMain.canScrollNext();
    if (this.refs.prevButton) this.refs.prevButton.disabled = !canScrollPrev;
    if (this.refs.nextButton) this.refs.nextButton.disabled = !canScrollNext;
  };

  /**
   * @param {number} index
   */
  #setActiveClasses(index) {
    const slides = this.refs.viewerSlides ?? [];
    const thumbs = this.refs.thumbnailButtons ?? [];

    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('media-gallery__slide--active', slideIndex === index);
    });

    thumbs.forEach((button, thumbIndex) => {
      const isActive = thumbIndex === index;
      button.classList.toggle('media-gallery__thumb--active', isActive);
      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  /**
   * @param {number} index
   */
  #announceSlide(index) {
    const liveRegion = this.refs.liveRegion;
    if (!liveRegion) return;
    const total = Number(this.dataset.mediaCount || this.refs.viewerSlides?.length || 0);
    const template = this.dataset.liveRegionText || 'Image [index] of [count]';
    liveRegion.textContent = template.replace('[index]', String(index + 1)).replace('[count]', String(total || 0));
  }

  /**
   * Scrolls by an offset, supporting Embla and fallback mode.
   * @param {number} offset
   */
  #scrollBy(offset) {
    const slides = this.refs.viewerSlides ?? [];
    if (!slides.length) return;

    const currentIndex =
      this.#emblaMain?.selectedScrollSnap?.() ??
      slides.findIndex((slide) => slide.classList.contains('media-gallery__slide--active'));

    const normalizedIndex = currentIndex >= 0 ? currentIndex : 0;
    const targetIndex = (normalizedIndex + offset + slides.length) % slides.length;
    this.#scrollToIndex(targetIndex);
  }

  /**
   * Scrolls to a specific slide index.
   * @param {number} index
   */
  #scrollToIndex(index) {
    if (this.#emblaMain) {
      this.#emblaMain.scrollTo(index);
    } else {
      this.#setActiveClasses(index);
      this.#announceSlide(index);
    }
  }
}

if (!customElements.get('media-gallery-component')) {
  customElements.define('media-gallery-component', MediaGalleryComponent);
}


